import { z } from 'zod';
import { FabricFacesSchema } from './fabric-faces.schema';
import {
  BanquettePolylineSchema,
  BanquetteRailSchema,
  BanquetteAdditionalPanelSchema,
  PanelRoleEnum,
} from './banquette-base-template.schema';
import {
  BackCushionDesignTypeEnum,
  BackCushionSpringConfigSchema,
} from './banquette-back-cushion-template.schema';
import { PanelFinishSchema } from './panel-finish.schema';
import { FoamDimLinkSchema } from './upholstery-design.schema';

// ── Upholstery Design ──
// Reuses BackCushionDesignTypeEnum — same upholstery design types apply to
// seat cushions (plain, channel-tufted, button-tufted, etc.).
export { BackCushionDesignTypeEnum as SeatCushionDesignTypeEnum };

export const SeatCushionDesignSchema = z.object({
  type: BackCushionDesignTypeEnum.default('plain'),
  channel_width: z.number().min(0).nullable().default(null),
  channel_count_override: z.number().int().min(0).nullable().default(null),
  button_tuftings_per_foot: z.number().int().min(0).nullable().default(null),
  button_hardware_id: z.string().uuid().nullable().default(null),
  wire_hardware_id: z.string().uuid().nullable().default(null),
});

// ── Spring Config ──
// Reuses BackCushionSpringConfigSchema — no-sag springs are configured
// identically on seat and back cushions (hardware, spacing/count mode, clips,
// connecting wire). Sharing the schema keeps the two from drifting.
export { BackCushionSpringConfigSchema as SeatCushionSpringConfigSchema };

// ── Backer Panel ──

export const SeatCushionBackerPanelSchema = z.object({
  material_id: z.string().uuid().nullable().default(null),
  waste_factor: z.number().min(0).max(1).default(0.10),
});

// ── Framing (reuses rail/panel schemas from banquette base) ──

export const SeatCushionFramingSchema = z.object({
  side_panel_material_id: z.string().uuid().nullable().default(null),
  rib_material_id: z.string().uuid().nullable().default(null),
  /** Profile used by any finish-enabled panel that does not name its own. */
  default_finish_id: z.string().uuid().nullable().default(null),
  side_panel_finish: PanelFinishSchema,
  rib_finish: PanelFinishSchema,
  rib_mode: z.enum(['auto', 'manual']).default('auto'),
  rib_spacing: z.number().min(0).default(24),
  rib_count: z.number().int().min(0).default(0),
  waste_factor: z.number().min(0).max(1).default(0.10),
  curved_section_count: z.number().int().min(0).default(0),
  curved_section_waste_factor: z.number().min(0).max(1).default(0),
  rails: z.array(BanquetteRailSchema).default([]),
  additional_panels: z.array(BanquetteAdditionalPanelSchema).default([]),
});

// ── Foam Layer ──

export const SeatCushionFoamLayerSchema = z.object({
  name: z.string(),
  width: z.number().min(0),          // width in inches (nesting dimension)
  /** When set, `width` is the base and tracks this component axis. */
  width_link: FoamDimLinkSchema,
  /** null = Auto: the layer runs the component's full width (lf_quantity * 12). */
  length: z.number().min(0).nullable().default(null),
  /** Only consulted when `length` is a number. */
  length_link: FoamDimLinkSchema,
  material_id: z.string().uuid().nullable(),
});

// ── Slot (reuses polyline schema from banquette base) ──

export const SeatCushionSlotSchema = z.object({
  id: z.string(),
  name: z.string(),
  material_id: z.string().uuid().nullable(),
  color: z.string(),
  calc_mode: z.enum(['linear', 'area']).default('linear'),
  panel_role: PanelRoleEnum.default('front'),
  polylines: z.array(BanquettePolylineSchema).default([]),
  finish_id_side1: z.string().uuid().nullable().optional(),
  finish_id_side2: z.string().uuid().nullable().optional(),
  // Fabric-specific (only used when material has fabric_subtype)
  fabric_wrap_value: z.number().nullable().default(null),
  fabric_staple_allowance: z.number().default(1),
});

// ── Main Config ──
//
// Differs from BackCushionConfig:
//   - product_type literal is 'banquette_seat_cushion'
//   - Springs are shared with back cushion (see SeatCushionSpringConfigSchema)
//   - Defaults reflect typical seat geometry: thinner profile_height (4"),
//     deeper profile_depth (20")
export const SeatCushionConfigSchema = z.object({
  product_type: z.literal('banquette_seat_cushion'),
  code: z.string(),
  name: z.string(),
  lf_quantity: z.number().min(0),
  manual_sections: z.boolean().default(false),
  section_count: z.number().int().min(1).default(1),
  profile_height: z.number().min(0).default(4),
  profile_depth: z.number().min(0).default(20),
  /** Reference depth the dynamic panels were authored against. Null = never
   *  baselined, which makes every delta zero. Must never auto-follow the live
   *  dimension — see the spec's freezing rule. */
  base_depth: z.number().min(0).nullable().default(null),
  /** Reference height the dynamic panels were authored against. */
  base_height: z.number().min(0).nullable().default(null),
  /** Reference width the dynamic foam layers were authored against. Null =
   *  never baselined, which makes every delta zero. Must never auto-follow the
   *  live dimension — see the spec's freezing rule. */
  base_width: z.number().min(0).nullable().default(null),

  // Design
  design: SeatCushionDesignSchema,

  // Substrate
  substrate_mode: z.enum(['simple_backer', 'full_frame', 'both']).default('full_frame'),
  backer_panel: SeatCushionBackerPanelSchema.nullable().default(null),
  framing: SeatCushionFramingSchema,

  // Foam layers (each layer can be a different foam type)
  foam_layers: z.array(SeatCushionFoamLayerSchema).default([]),
  foam_waste_factor: z.number().min(0).max(1).default(0.10),
  /** Extra foam waste on curved sections only, blended by the curved share of
   *  the item — matches framing.curved_section_waste_factor. */
  foam_curved_waste_factor: z.number().min(0).max(1).default(0),
  dacron_material_id: z.string().uuid().nullable().default(null),
  dacron_waste_factor: z.number().min(0).max(1).default(0.10),
  /** Extra dacron waste on curved sections only. */
  dacron_curved_waste_factor: z.number().min(0).max(1).default(0),

  // Springs
  // Defaulted, unlike back cushion's required `springs`: no seat cushion saved
  // before this feature has the key at all, and there is no migration to add
  // it. A missing block parses to all-defaults, i.e. springs off.
  springs: BackCushionSpringConfigSchema.default({}),

  // Per-line-item fabric allowances
  fabric_design_pct: z.number().min(0).nullable().optional(),
  fabric_pattern_allowance_pct: z.number().min(0).default(0),
  /** Which box faces receive fabric. Absent means all six — see resolveFaces. */
  fabric_faces: FabricFacesSchema.optional(),
  /** Whether the Oversized Rules factor applies. Absent means on — only an
   *  explicit false opts out (see resolveOversize). Deliberately not
   *  defaulted: the Cria price API reads configs as raw JSON with no schema
   *  defaults, so "absent is on" has to hold at the reader regardless. */
  oversize_enabled: z.boolean().optional(),

  // Slots
  viewport_id: z.string().uuid().nullable().optional(),
  image_scale_ppi: z.number().min(0).default(0),
  slots: z.array(SeatCushionSlotSchema).default([]),
}).superRefine((cfg, ctx) => {
  const r = cfg.framing?.curved_section_count ?? 0;
  if (r > cfg.section_count) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Curved sections cannot exceed total sections',
      path: ['framing', 'curved_section_count'],
    });
  }
});

// ── Template CRUD ──

export const SeatCushionTemplateSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  seat_cushion_config: z.string(),
  active: z.boolean(),
  created_by: z.string().uuid().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const CreateSeatCushionTemplateSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable().optional(),
  seat_cushion_config: z.string().default('{}'),
  active: z.boolean().default(true),
});

export const UpdateSeatCushionTemplateSchema = CreateSeatCushionTemplateSchema.partial();

// ── Type exports ──

export type SeatCushionDesignType = z.infer<typeof BackCushionDesignTypeEnum>;
export type SeatCushionDesign = z.infer<typeof SeatCushionDesignSchema>;
export type SeatCushionSpringConfig = z.infer<typeof BackCushionSpringConfigSchema>;
export type SeatCushionBackerPanel = z.infer<typeof SeatCushionBackerPanelSchema>;
export type SeatCushionFraming = z.infer<typeof SeatCushionFramingSchema>;
export type SeatCushionFoamLayer = z.infer<typeof SeatCushionFoamLayerSchema>;
export type SeatCushionSlot = z.infer<typeof SeatCushionSlotSchema>;
export type SeatCushionConfig = z.infer<typeof SeatCushionConfigSchema>;
export type SeatCushionTemplate = z.infer<typeof SeatCushionTemplateSchema>;
export type CreateSeatCushionTemplate = z.infer<typeof CreateSeatCushionTemplateSchema>;
export type UpdateSeatCushionTemplate = z.infer<typeof UpdateSeatCushionTemplateSchema>;
